import React from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { motion } from "framer-motion";
import AuthModal from "./AuthModal";
import ContactModal from "./ContactModal";
import { FONTS, LANDING_COMPACT_NAV, LANDING_MOBILE, MEDIA_URL } from "./landingTheme";
import { EIcon, MIcon } from "./LogoIcons";

/* ─────────────────────── HERO SECTION ─────────────────────── */
const HeroSection = ({ colors, onOpenAuth, isAuthModalOpen, onCloseAuth, onOpenContact, isContactModalOpen, onCloseContact }) => {
    // Celular: card ocupa a largura toda, título menor e botões empilhados
    const isMobile = useMediaQuery(LANDING_MOBILE);
    const isTablet = useMediaQuery(LANDING_COMPACT_NAV) && !isMobile;
    // =========================================================================
    // ⚙️ HERO LAYOUT CONFIGURATIONS
    // =========================================================================
    const VIDEO_BRIGHTNESS = 0.5; // Background video brightness (e.g. 1 = 100%, 0.5 = 50%, 0 = 0%)

    // Shift the entire content block up or down (in px)
    // Positive values move down, negative values move up
    const CONTENT_BLOCK_Y_OFFSET = 0;

    // Independent vertical offsets (in px) for each element.
    // Changing these will move the elements vertically without affecting the others.
    // Positive values move down, negative values move up.
    const SUBTITLE_Y_OFFSET = -30; // Vertical offset for "Entertainment Maintenance"
    const TITLE_Y_OFFSET = 0;    // Vertical offset for "Gestão inteligente para..."
    const BUTTONS_Y_OFFSET = 30;  // Vertical offset for the button container
    const BUTTONS_HORIZONTAL_SPACING = isMobile ? 12 : isTablet ? 24 : 100; // Distância horizontal entre os dois botões (em px); no tablet 100px não cabe e eles quebravam linha

    // Configurações de tipografia do Título Principal
    const TITLE_LETTER_SPACING = isMobile ? 0 : 2; // Espaçamento entre as letras (em px ou em)
    const TITLE_MAX_WIDTH = 2000; // Largura máxima que o texto vai ocupar (em px)

    // Ajustes finos (X e Y) para as letras customizadas (M e E)
    const ICON_M_X = -4; // Move o 'M' para esquerda/direita (ex: -5 ou 5)
    const ICON_M_Y = 1; // Move o 'M' para cima/baixo (ex: -2 ou 2)

    const ICON_E_X = -4; // Move o 'E' para esquerda/direita
    const ICON_E_Y = 1; // Move o 'E' para cima/baixo
    // =========================================================================

    return (
        <section style={{
            position: "relative",
            zIndex: 10, // Elevates the Hero so it can hide elements sliding from underneath
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: isMobile ? "128px 12px 40px" : "160px 24px 80px",
            boxSizing: "border-box",
            width: "100%",
            marginBottom: 0
        }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.25, 0.4, 0.25, 1] }}
                style={{
                    width: isMobile ? "100%" : "calc(100% - 48px)", // Forces same lateral spacing as Navbar
                    maxWidth: 1300,             // Locked to same header width
                    height: isMobile ? "auto" : 680,
                    minHeight: isMobile ? 520 : undefined,
                    borderRadius: isMobile ? 28 : 40, background: colors.black, overflow: "hidden",
                    position: "relative", display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", padding: isMobile ? "48px 16px" : "40px",
                    boxSizing: "border-box"
                }}
            >
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                        filter: `brightness(${VIDEO_BRIGHTNESS})`
                    }}
                >
                    <source src={`${MEDIA_URL}/Hero%20video.mp4`} type="video/mp4" />
                </video>

                <div style={{
                    position: "relative",
                    zIndex: 2,
                    textAlign: "center",
                    maxWidth: 900,
                    transform: `translateY(${CONTENT_BLOCK_Y_OFFSET}px)`
                }}>
                    <motion.p
                        initial={{ opacity: 0, y: SUBTITLE_Y_OFFSET + 20 }}
                        animate={{ opacity: 1, y: SUBTITLE_Y_OFFSET }}
                        transition={{ delay: 0.3 }}
                        style={{
                            fontFamily: FONTS.body,
                            fontSize: isMobile ? 13 : 20,
                            fontWeight: 600,
                            color: "rgba(255,255,255,0.7)",
                            textTransform: "uppercase",
                            letterSpacing: 2,
                            marginTop: 0,
                            marginBottom: 24 // Stable default spacing
                        }}
                    >
                        Entertainment Maintenance
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: TITLE_Y_OFFSET + 30 }}
                        animate={{ opacity: 1, y: TITLE_Y_OFFSET }}
                        transition={{ delay: 0.5 }}
                        style={{
                            fontFamily: `'CustomHeroFont', ${FONTS.heading}`,
                            fontSize: isMobile ? "clamp(26px, 8.4vw, 40px)" : "clamp(40px, 6vw, 76px)",
                            fontWeight: 800,
                            color: colors.white,
                            lineHeight: 1.15,
                            letterSpacing: TITLE_LETTER_SPACING,
                            marginTop: 0,
                            marginBottom: isMobile ? 28 : 48, // Stable default spacing
                            marginLeft: "auto",
                            marginRight: "auto",
                            maxWidth: TITLE_MAX_WIDTH
                        }}
                    >
                        Gestão inteligente para &nbsp;<span style={{ display: "inline-flex", alignItems: "baseline", whiteSpace: "nowrap" }}><MIcon style={{ marginRight: TITLE_LETTER_SPACING, transform: `translate(${ICON_M_X}px, ${ICON_M_Y}px)` }} />áquinas</span> de <span style={{ display: "inline-flex", alignItems: "baseline", whiteSpace: "nowrap" }}><EIcon style={{ marginRight: TITLE_LETTER_SPACING, transform: `translate(${ICON_E_X}px, ${ICON_E_Y}px)` }} />ntretenimento</span>
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0, y: BUTTONS_Y_OFFSET + 20 }}
                        animate={{ opacity: 1, y: BUTTONS_Y_OFFSET }}
                        transition={{ delay: 0.7 }}
                        style={{
                            display: "flex",
                            columnGap: BUTTONS_HORIZONTAL_SPACING,
                            rowGap: 12, // se quebrarem linha, não herdam o espaçamento horizontal
                            justifyContent: "center",
                            flexWrap: "wrap",
                            flexDirection: isMobile ? "column" : "row",
                            marginTop: 0
                        }}
                    >
                        <motion.button
                            onClick={onOpenAuth}
                            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                            style={{
                                fontFamily: FONTS.body, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: colors.black,
                                background: colors.white, border: "none", borderRadius: 50,
                                padding: isMobile ? "14px 24px" : "18px 40px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12,
                            }}
                        >
                            Conhecer o Sistema
                        </motion.button>
                        <motion.button
                            onClick={onOpenContact}
                            whileHover={{ scale: 1.04, borderColor: "rgba(255, 255, 255, 0.8)", backgroundColor: "rgba(255, 255, 255, 0.08)" }} whileTap={{ scale: 0.96 }}
                            style={{
                                fontFamily: FONTS.body, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: colors.white,
                                background: "transparent", border: "2px solid rgba(255, 255, 255, 0.3)", borderRadius: 50,
                                padding: isMobile ? "12px 22px" : "16px 38px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12,
                            }}
                        >
                            Falar com um Especialista
                        </motion.button>
                    </motion.div>
                </div>
                <AuthModal isOpen={isAuthModalOpen} onClose={onCloseAuth} colors={colors} />
                <ContactModal isOpen={isContactModalOpen} onClose={onCloseContact} colors={colors} />
            </motion.div>

        </section>
    );
};

export default HeroSection;

